import * as React from 'react';
import { InviteGetAdditionalDatasheetFormatDataParams } from "@common/actions/invites";
import { InviteDatasheetAdditionalDataUpdateDto } from "@common/dto/invite/datasheet/additional-data-update-dto";
import { InviteDatasheetFormatGetDto } from "@common/dto/invite/datasheet/format-get-dto";
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';
import { HttpErrorDto } from '@common/dto/http';
import { I18n } from 'react-redux-i18n';
import { DatasheetFieldComponentGetDto } from '@common/dto/datasheet/field-component/get-dto';
import { FolderContext } from '@common/dto/folder/context';

export type AdditionalPatientDetailsProps = {
  showErrorMessage: (msg: string) => void;
  questionnaire: QuestionnaireListDto;
  globalId: number;
  onChange: (data: Array<InviteDatasheetAdditionalDataUpdateDto>) => void;
  getAdditionalData: (params: InviteGetAdditionalDatasheetFormatDataParams) => Promise<InviteDatasheetFormatGetDto>
};

export const useAdditionalPatientDetailsController = (props: AdditionalPatientDetailsProps) => {
  const { onChange, getAdditionalData, questionnaire, globalId, showErrorMessage } = props;
  const [defaultDsAdditionalData, setDefaultDsAdditionalData] = React.useState<InviteDatasheetFormatGetDto | undefined>(undefined);

  const [value, setValue] = React.useState<Record<string, InviteDatasheetAdditionalDataUpdateDto> | undefined>(undefined);

  React.useEffect(() => {
    getAdditionalData({
      questionnaireId: questionnaire.questionnaireId,
      fhq4: questionnaire.fhq4,
      globalId: globalId,
    }).then((data) => {
      setDefaultDsAdditionalData(data);
    }).catch((err) => {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to get additional data'));
    });
  }, []);

  const createLookup = (params: { 
    fieldId: InviteDatasheetAdditionalDataUpdateDto['fieldId']; 
    context: InviteDatasheetAdditionalDataUpdateDto['context'];
    system: InviteDatasheetAdditionalDataUpdateDto['system'];
  }) => {
    return `${params.fieldId}_${params.context}_${params.system}`;
  };

  const handleFieldChange = React.useCallback((dsField: DatasheetFieldComponentGetDto, fieldValue: string) => {
    const lookup = createLookup({
      fieldId: dsField.field.fieldid,

      // TODO remove hardcode of context

      context: FolderContext.individual,
      system: dsField.field.systemField,
    });

    const newValue = {
      ...value,
      [lookup]: {
        globalId: globalId,
        dataValue: fieldValue,
        fieldId: dsField.field.fieldid,
        context: FolderContext.individual,
        system: dsField.field.systemField,
      }
    };

    setValue(newValue);
    onChange(Object.values(newValue));
  }, [onChange, value]);

  const getFieldValue = (field: DatasheetFieldComponentGetDto['field']) => {
    const lookup = createLookup({
      fieldId: field.fieldid,
      context: FolderContext.individual,
      system: field.systemField,
    });

    return value?.[lookup]?.dataValue;
  };

  return {
    handleFieldChange,
    getFieldValue,
    defaultDsAdditionalData,
  };
};